import React, { useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send, Pencil, Save, X } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { timeAgo } from "@/lib/communities";

const CATS = ["General", "Player Report", "Ban Appeal", "Server Issue", "Kit Issue", "Payment Issue", "Bug Report"];

export default function Support() {
    const { data: tickets, reload } = useFetch("/tickets");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [form, setForm] = useState({ category: "General", subject: "", message: "" });
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState({});
    if (!tickets) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });

    const create = async () => {
        if (!form.subject || !form.message) { toast.error("Subject and message required"); return; }
        try { await api.post("/tickets", { ...form, post_to_discord: true }); toast.success("Ticket submitted"); setOpen(false); setForm({ category: "General", subject: "", message: "" }); reload(); }
        catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };
    const sendReply = async (t) => {
        if (!reply.trim()) return;
        const { data } = await api.post(`/tickets/${t.id}/reply`, { message: reply });
        setReply(""); setActive(data); reload();
    };
    const startEdit = () => { setEdit({ subject: active.subject, category: active.category, message: active.message }); setEditing(true); };
    const saveEdit = async () => {
        try {
            const { data } = await api.patch(`/tickets/${active.id}`, edit);
            toast.success("Ticket updated"); setActive(data); setEditing(false); reload();
        } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };

    return (
        <div data-testid="player-support">
            <PageHeader title="Support" subtitle="Open a ticket with the staff team. You can edit a ticket while it's still open."
                action={<Button data-testid="new-ticket-btn" onClick={() => setOpen(!open)} className="font-heading uppercase bg-hazard text-black hover:bg-hazard/90"><Plus size={15} className="mr-1" /> New Ticket</Button>} />
            {open && (
                <SectionCard title="New Ticket" className="mb-5">
                    <div className="space-y-3">
                        <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                            <SelectTrigger data-testid="ticket-category" className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input data-testid="ticket-subject" placeholder="Subject" value={form.subject} onChange={(e) => upd("subject", e.target.value)} className="bg-background border-border" />
                        <Textarea data-testid="ticket-message" placeholder="Describe your issue" value={form.message} onChange={(e) => upd("message", e.target.value)} className="bg-background border-border" rows={3} />
                        <Button data-testid="ticket-submit" onClick={create} className="font-heading uppercase bg-radioactive/90 text-black hover:bg-radioactive">Submit Ticket</Button>
                    </div>
                </SectionCard>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-5">
                <div className="space-y-2">
                    {tickets.map((t) => (
                        <button key={t.id} data-testid={`ticket-${t.id}`} onClick={() => { setActive(t); setEditing(false); }}
                            className={`w-full text-left tactical-card rounded-md p-3 ${active?.id === t.id ? "border-hazard/50" : ""}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-heading font-semibold uppercase text-foreground">{t.subject}</span>
                                <StatusBadge status={t.status === "closed" ? "offline" : t.status === "open" ? "warning" : "active"} label={t.status.replace("_", " ").toUpperCase()} />
                            </div>
                            <div className="label-mono text-muted-foreground mt-1">{t.category} · {timeAgo(t.created_at)}</div>
                        </button>
                    ))}
                    {tickets.length === 0 && <div className="text-muted-foreground">No tickets yet.</div>}
                </div>
                {active && (
                    <SectionCard title={editing ? "Edit Ticket" : active.subject} testId="ticket-detail"
                        action={
                            active.status !== "closed" && (
                                editing ? (
                                    <div className="flex items-center gap-1.5">
                                        <Button data-testid="my-ticket-edit-save" size="icon" className="h-8 w-8 bg-radioactive/90 text-black hover:bg-radioactive" onClick={saveEdit}><Save size={14} /></Button>
                                        <Button data-testid="my-ticket-edit-cancel" size="icon" variant="outline" className="h-8 w-8 border-border" onClick={() => setEditing(false)}><X size={14} /></Button>
                                    </div>
                                ) : (
                                    <Button data-testid="my-ticket-edit" size="icon" variant="outline" className="h-8 w-8 border-border hover:border-hazard/50 hover:text-hazard" onClick={startEdit}><Pencil size={14} /></Button>
                                )
                            )
                        }>
                        {editing ? (
                            <div className="space-y-3">
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Subject</div>
                                    <Input data-testid="my-ticket-edit-subject" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="bg-background border-border" />
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Category</div>
                                    <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                                        <SelectTrigger data-testid="my-ticket-edit-category" className="bg-background border-border"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Message</div>
                                    <Textarea data-testid="my-ticket-edit-message" rows={4} value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} className="bg-background border-border" />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 mb-3"><LifeBuoy size={15} className="text-hazard" /><span className="label-mono text-muted-foreground">{active.category}</span><StatusBadge status={active.status === "closed" ? "offline" : "active"} label={active.status.replace("_", " ").toUpperCase()} /></div>
                                <p className="text-sm text-foreground rounded-md border border-border bg-background/40 p-3 whitespace-pre-wrap">{active.message}</p>
                                <div className="space-y-2 mt-3 max-h-64 overflow-y-auto">
                                    {(active.replies || []).map((r, i) => (
                                        <div key={`${r.created_at}-${i}`} className={`rounded-md border p-2.5 ${r.is_staff ? "border-hazard/40 bg-hazard/10" : "border-border bg-background/40"}`}>
                                            <div className="label-mono text-muted-foreground">{r.author} {r.is_staff ? "· STAFF" : ""} · {timeAgo(r.created_at)}</div>
                                            <p className="text-sm text-foreground mt-0.5">{r.message}</p>
                                        </div>
                                    ))}
                                </div>
                                {active.status !== "closed" && (
                                    <div className="flex gap-2 mt-3">
                                        <Input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="bg-background border-border" />
                                        <Button data-testid="ticket-reply-send" onClick={() => sendReply(active)} className="bg-hazard text-black hover:bg-hazard/90"><Send size={15} /></Button>
                                    </div>
                                )}
                            </>
                        )}
                    </SectionCard>
                )}
            </div>
            <Footer />
        </div>
    );
}
